import React, { useState, useEffect } from 'react';
import { RefreshCw, User } from 'lucide-react';
import authService from '../services/AuthService';
import InfoTooltip from '../components/InfoTooltip';
import { MARKETPLACE_LOGOS, getSupplierLogo, API_BASE } from '../consts';
import { calculateReferralFee, calculateWFSFee, detectPackCount } from '../utils';
import FavoriteButton from './FavoriteButton';
import { CopyButton, PackSizeButton, FeeSettings, KeepaSettingsModal, MultiSourcePopup, CashbackModal } from './DealComponents';
import UniversalFavoriteButton from './UniversalFavoriteButton';
import FindBundlesButton from './FindBundlesButton';

const DealCard = ({ deal, alternativeSources = [], couponAmount = 0, fulfillmentType = null, onFulfillmentChange = null, onRefreshGated, refreshing, onSettingsChange, onHideDeal, amazonLinked = false, autoPackDetection = true }) => {
    const [showFeeSettings, setShowFeeSettings] = useState(false);
    const [feeSettings, setFeeSettings] = useState({});
    const [showKeepaSettings, setShowKeepaSettings] = useState(false);
    const [keepaChartCache, setKeepaChartCache] = useState({});
    const [keepaChartLoading, setKeepaChartLoading] = useState(false);
    const [triggeringReverseScan, setTriggeringReverseScan] = useState(false);
    const [showMultiSourcePopup, setShowMultiSourcePopup] = useState(false);
    const initialPack = (() => {
        try {
            const pack = deal.marketplace_data?.pack;
            if (pack && pack > 1) {
                const sp = deal.source_price || 0;
                const azPrice = deal.marketplace_data?.az_price || 0;
                const fbaFee = deal.marketplace_data?.fba_fee || 0;
                const refFee = azPrice * (deal.marketplace_data?.ref_fee_perc || 0.15);
                const roi = sp > 0 ? ((azPrice - sp - fbaFee - refFee) / sp) * 100 : 0;
                if (roi > 100) return pack;
            }
            // Backend pack not usable — fall back to title detection
            if (!autoPackDetection) return 1;
            const titleToCheck = deal.marketplace === 'amazon'
                ? deal.marketplace_data?.title || deal.title || ''
                : deal.marketplace === 'walmart'
                    ? deal.marketplace_data?.name || deal.marketplace_data?.title || deal.title || ''
                    : deal.title || '';
            return detectPackCount(titleToCheck);
        } catch {
            return 1;
        }
    })();
    const [packSize, setPackSize] = useState(initialPack);
    const [showCashbackModal, setShowCashbackModal] = useState(false);
    const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false);
    const [showSearchingPopup, setShowSearchingPopup] = useState(false);

    // Load price overrides from localStorage
    const priceOverrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');
    const dealOverride = priceOverrides[deal.id] || {};

    // Apply overrides to deal object
    if (dealOverride.source_price !== undefined) {
        deal.source_price = dealOverride.source_price;
    }
    if (dealOverride.sell_price !== undefined) {
        const data = deal.marketplace_data || {};
        if (deal.marketplace === 'amazon') {
            data.az_price = dealOverride.sell_price;
        } else if (deal.marketplace === 'walmart') {
            data.price_value = dealOverride.sell_price;
        } else if (deal.marketplace === 'ebay') {
            data.lowest_price = dealOverride.sell_price;
        }
    }

    // Check if there are multiple sources
    const hasMultipleSources = alternativeSources.length > 0;
    const allSources = [deal, ...alternativeSources];
    const totalSources = allSources.length;
    const openUrl = deal.product_url || (deal.source_link && /^https?:\/\//i.test(deal.source_link) ? deal.source_link : null);

    const supplierLogo = getSupplierLogo(deal.supplier_name);
    const marketplaceLogo = MARKETPLACE_LOGOS[deal.marketplace];

    useEffect(() => {
        setPackSize(initialPack);
    }, [autoPackDetection]);

    useEffect(() => {
        const storageKey = `${deal.marketplace}_fee_settings`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            setFeeSettings(JSON.parse(saved));
        }
    }, [deal.marketplace]);

    const handleRevealClick = (e) => {
        e.preventDefault();
        const upc = deal.upc;
        const hasValidUpc = upc && String(upc).trim().replace(/\s/g, '').match(/^\d+$/);

        if (!hasValidUpc) {
            if (!localStorage.getItem('no_upc_alert_shown')) {
                alert('ℹ️ Important: When we don\'t have enough data to run our algorithm (such as a valid UPC), we will automatically open a manual Google search for you instead.\n\nThis message will not be shown again.');
                localStorage.setItem('no_upc_alert_shown', 'true');
            }
            const title = deal.title || deal.marketplace_data?.title || deal.marketplace_data?.name || '';
            if (title) {
                const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                const EXTidentifier = deal.marketplace === 'amazon' ? (
                    deal?.marketplace_data?.asin
                ) : (
                    deal.marketplace === 'walmart' ? (
                        deal?.marketplace_data?.item_id
                    ) : (
                        deal.marketplace === 'ebay' ? (
                            deal?.marketplace_data?.upc
                        ) : ''
                    )
                );
                if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                    window.chrome.runtime.sendMessage(EXT_ID, {
                        type: 'MSP_SEARCH_INTENT',
                        marketplace: deal.marketplace,
                        identifier: EXTidentifier,
                        title: deal.title,
                        deal,
                    });
                }
                window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
            }
            return;
        }

        // Instead of triggering single deal, fire the global reveal all flow
        if (window.__msp_openSupplierPicker) {
            window.__msp_openSupplierPicker();
        }
    };

    const handleTriggerReverseScan = async (dealId) => {
        if (triggeringReverseScan) return;

        setTriggeringReverseScan(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + (window.location.pathname.includes('reverse-scan') ? '/api/reverse-scan/deals/trigger' : '/api/storefront/reverse-scan/trigger'),
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ deal_ids: [dealId] })
                }
            );

            if (response.ok) {
                deal.reverse_scan_status = 'pending';
                if (onSettingsChange) {
                    onSettingsChange();
                }
            } else {
                const data = await response.json();
                alert(data.error || 'Failed to trigger reverse scan');
            }
        } catch (error) {
            console.error('Error triggering reverse scan:', error);
            alert('Failed to trigger reverse scan');
        } finally {
            setTriggeringReverseScan(false);
        }
    };

    const cleanTitle = (title) => {
        if (!title) return '';
        return title.replace(/\(Seller:\s*[^)]+\)\s*/gi, '').trim();
    };

    const getSellerNote = () => {
        const titleLower = (deal.title || '').toLowerCase();
        const isWalmartSupplier = deal.supplier_name?.toLowerCase() === 'walmart';
        const isAmazonSupplier = deal.supplier_name?.toLowerCase() === 'amazon';

        const hasWalmartSeller = titleLower.includes('(seller: walmart.com)') || titleLower.includes('(seller: walmart)');

        const isA2aDomain = window.location.href.includes('a2a-deals');
        const marketplaceTitleLower = isA2aDomain ? (deal.marketplace_data?.title || '').toLowerCase() : '';

        const hasAmazonSeller =
            titleLower.includes('(seller: amazon.com)') ||
            titleLower.includes('(seller: amazon)') ||
            (isA2aDomain && (
                marketplaceTitleLower.includes('(seller: amazon.com)') ||
                marketplaceTitleLower.includes('(seller: amazon)')
            ));

        if (isWalmartSupplier && titleLower.includes('(seller:')) {
            if (hasWalmartSeller) {
                return { type: 'walmart', note: '✓ Walmart is the seller', isGood: true };
            } else {
                return { type: 'walmart', note: '⚠️ Walmart is NOT the seller', isGood: false };
            }
        }

        if (isAmazonSupplier) {
            if (hasAmazonSeller) {
                return { type: 'amazon', note: '✓ Amazon is the seller', isGood: true };
            } else {
                return { type: 'amazon', note: '⚠️ Amazon is NOT the seller', isGood: false };
            }
        }

        return null;
    };

    const sellerNote = getSellerNote();
    const cleanedTitle = cleanTitle(deal.title);

    const getGatedBadge = (status, asin) => {
        const handleClick = () => {
            if (asin && (status === 'gated' || status === 'approval' || status === 'checking')) {
                window.open(
                    `https://sellercentral.amazon${amazonDomain}/hz/approvalrequest/restrictions/approve?asin=${asin}`,
                    "_blank"
                );
            }
        };

        switch (status) {
            case 'ungated':
                return (
                    <span
                        className="text-green-900 bg-green-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-green-200 transition border border-green-400"
                        onClick={() => asin && window.open(`https://sellercentral.amazon${amazonDomain}/product-search/search?q=${asin}`, "_blank")}
                        title="Click to view on Seller Central"
                    >
                        ✓ Ungated
                    </span>
                );
            case 'approval':
                return (
                    <span
                        className="text-orange-900 bg-orange-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-orange-200 transition border border-orange-400"
                        onClick={handleClick}
                        title="Click to apply for approval on Seller Central"
                    >
                        ⚠ Approval
                    </span>
                );
            case 'gated':
                return (
                    <span
                        className="text-red-900 bg-red-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-red-200 transition border border-red-500"
                        onClick={handleClick}
                        title="Click to check restrictions on Seller Central"
                    >
                        ✗ Gated
                    </span>
                );
            case 'checking':
            case 'error':
                return (
                    <span className="text-blue-900 bg-blue-100 px-2 py-1 rounded text-xs font-bold cursor-pointer hover:bg-blue-200 transition border border-blue-400"
                        onClick={handleClick}
                        title="Click to check restrictions on Seller Central"
                    >
                        Checking...
                    </span>
                );
            default:
                return false;
        }
    };

    const calculateMetrics = () => {
        const sourcePrice = (deal.source_price || 0) * (1 - (couponAmount / 100)) * packSize;
        const data = deal.marketplace_data || {};

        const itemWeight = data.weight || deal.weight || 1;
        const inboundShippingPerLb = feeSettings.inboundShipping || 0;
        const inboundShippingCost = inboundShippingPerLb * itemWeight;

        if (deal.marketplace === 'amazon') {
            const azPrice = data.az_price || 0;
            const fbaFee = fulfillmentType === 'fba' ? (data.fba_fee || 0) : 0;
            const refFeePerc = data.ref_fee_perc || 0.15;
            const refFee = azPrice * refFeePerc;
            const placementFees = fulfillmentType === 'fba' ? (feeSettings.placementFees || 0) : 0;
            const inboundShipping = fulfillmentType === 'fba' ? inboundShippingCost : 0;
            const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            const otherCosts = feeSettings.otherCosts || 0;
            const fulfillmentCost = fulfillmentType !== 'fba' ? (feeSettings.fulfillmentCost || 0) : 0;

            const totalFees = fbaFee + refFee + placementFees + inboundShipping + salesTaxAmount + otherCosts + fulfillmentCost;
            const profit = azPrice - sourcePrice - totalFees;
            const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

            return {
                sellPrice: azPrice,
                totalFees,
                profit,
                roi,
                breakdown: { fbaFee, refFee, refFeePerc: refFeePerc * 100, placementFees, inboundShipping, salesTax: salesTaxAmount, otherCosts, fulfillmentCost }
            };
        } else if (deal.marketplace === 'walmart') {
            const wmPrice = data.price_value || 0;

            let wfsFee = 0;
            if (fulfillmentType === 'wfs') {
                if (data.dimensions && itemWeight) {
                    wfsFee = calculateWFSFee(
                        data.dimensions,
                        itemWeight,
                        {
                            isApparel: data.category?.toLowerCase().includes("clothing") ||
                                data.category?.toLowerCase().includes("apparel") ||
                                data.sub_category?.toLowerCase().includes("clothing") ||
                                data.sub_category?.toLowerCase().includes("apparel"),
                            retailPrice: wmPrice
                        }
                    );
                } else {
                    wfsFee = 3.45;
                }
            }

            const refFee = calculateReferralFee(wmPrice, data.category, data.sub_category);
            const refFeePerc = wmPrice > 0 ? (refFee / wmPrice) * 100 : 15;
            const inboundShipping = fulfillmentType === 'wfs' ? inboundShippingCost : 0;
            const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            const otherCosts = feeSettings.otherCosts || 0;
            const fulfillmentCost = fulfillmentType !== 'wfs' ? (feeSettings.fulfillmentCost || 0) : 0;

            const totalFees = wfsFee + refFee + inboundShipping + salesTaxAmount + otherCosts + fulfillmentCost;
            const profit = wmPrice - sourcePrice - totalFees;
            const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

            return {
                sellPrice: wmPrice,
                totalFees,
                profit,
                roi,
                breakdown: {
                    wfsFee,
                    refFee,
                    refFeePerc,
                    inboundShipping,
                    salesTax: salesTaxAmount,
                    otherCosts,
                    fulfillmentCost
                },
                missingData: {
                    weight: !itemWeight,
                    dimensions: !data.dimensions || !data.dimensions.length || !data.dimensions.width || !data.dimensions.height,
                    category: !data.category
                }
            };
        } else if (deal.marketplace === 'ebay') {
            const ebayPrice = data.lowest_price || 0;
            const shippingCharge = 0;

            const totalSale = ebayPrice + shippingCharge;

            const categoryRates = {
                'most': 0.136,
                'books': 0.153,
                'movies': 0.153,
                'music': 0.153,
                'coins': 0.1325,
                'bullion': totalSale <= 7500 ? 0.136 : 0.07,
                'handbags': totalSale <= 2000 ? 0.15 : 0.09,
                'collectibles': 0.1325,
                'jewelry': totalSale <= 5000 ? 0.15 : 0.09,
                'watches': 0.15,
                'guitars': 0.067,
                'athletic_shoes': totalSale >= 150 ? 0.08 : 0.136,
                'nfts': 0.05
            };

            const categoryFromData = (data.category || 'most').toLowerCase();
            let category = 'most';
            if (categoryRates[categoryFromData]) {
                category = categoryFromData;
            }

            let baseFeeRate = categoryRates[category] || 0.136;

            const storeSubscription = feeSettings.storeSubscription || 'none';
            const isTopRated = feeSettings.topRated || false;

            const storeDiscounts = {
                'none': 0,
                'basic': 0.066,
                'premium': 0.088,
                'anchor': 0.102,
                'enterprise': 0.110
            };

            const storeDiscount = storeDiscounts[storeSubscription] || 0;
            const topRatedDiscount = isTopRated ? 0.10 : 0;
            const totalDiscount = storeDiscount + topRatedDiscount;

            let finalValueFee = 0;

            if (category === 'watches') {
                if (totalSale <= 1000) {
                    finalValueFee = totalSale * 0.15;
                } else if (totalSale <= 7500) {
                    finalValueFee = (1000 * 0.15) + ((totalSale - 1000) * 0.065);
                } else {
                    finalValueFee = (1000 * 0.15) + (6500 * 0.065) + ((totalSale - 7500) * 0.03);
                }
                finalValueFee = finalValueFee * (1 - totalDiscount);
            } else if (category === 'bullion' && totalSale > 7500) {
                finalValueFee = totalSale * 0.07;
                finalValueFee = finalValueFee * (1 - totalDiscount);
            } else {
                if (totalSale <= 7500) {
                    finalValueFee = totalSale * baseFeeRate;
                } else {
                    finalValueFee = (7500 * baseFeeRate) + ((totalSale - 7500) * 0.0235);
                }
                finalValueFee = finalValueFee * (1 - totalDiscount);
            }

            let perOrderFee = 0;
            if (category === 'athletic_shoes' && totalSale >= 150) {
                perOrderFee = 0;
            } else {
                perOrderFee = totalSale <= 10 ? 0.30 : 0.40;
            }

            const paymentProcessingFee = (totalSale * 0.0235) + 0.30;

            const shippingCost = 0;

            const salesTaxAmount = sourcePrice * ((feeSettings.salesTax || 0) / 100);
            const packagingCost = feeSettings.packagingCost || 0;
            const otherCosts = feeSettings.otherCosts || 0;

            const totalFees = finalValueFee + perOrderFee + paymentProcessingFee + shippingCost + salesTaxAmount + packagingCost + otherCosts;
            const profit = ebayPrice + shippingCharge - sourcePrice - totalFees;
            const roi = sourcePrice > 0 ? ((profit / sourcePrice) * 100) : 0;

            return {
                sellPrice: ebayPrice,
                totalFees,
                profit,
                roi,
                breakdown: {
                    finalValueFee,
                    perOrderFee,
                    paymentProcessingFee,
                    shippingCharge,
                    shippingCost,
                    salesTax: salesTaxAmount,
                    packagingCost,
                    otherCosts,
                    category
                }
            };
        }

        return { sellPrice: 0, totalFees: 0, profit: 0, roi: 0, breakdown: {} };
    };

    const fetchKeepaChart = async () => {
        if (deal.marketplace !== 'amazon' || !data.asin) return;

        const savedSettings = JSON.parse(localStorage.getItem('keepa_chart_settings') || '{"range":90,"salesrank":true,"amazon":true,"new":true,"bb":true,"fba":true}');
        const cacheKey = `${data.asin}_${savedSettings.range}_${savedSettings.salesrank}_${savedSettings.amazon}_${savedSettings.new}_${savedSettings.bb}_${savedSettings.fba}`;

        if (keepaChartCache[cacheKey]) {
            return;
        }

        setKeepaChartLoading(true);
        try {
            const params = new URLSearchParams({
                domain: { '.com': '1', '.co.uk': '2', '.ca': '6', '.com.mx': '11' }[amazonDomain] || '1',
                salesrank: savedSettings.salesrank ? '1' : '0',
                amazon: savedSettings.amazon ? '1' : '0',
                new: savedSettings.new ? '1' : '0',
                bb: savedSettings.bb ? '1' : '0',
                fba: savedSettings.fba ? '1' : '0',
                range: savedSettings.range.toString()
            });

            const url = `${API_BASE}/api/keepa/chart/${data.asin}?${params.toString()}`;
            const response = await authService.authenticatedFetch(url);
            const responseData = await response.json();

            if (responseData && responseData.url) {
                setKeepaChartCache(prev => ({ ...prev, [cacheKey]: responseData.url }));
            } else {
                console.error('Invalid response structure:', responseData);
                setKeepaChartCache(prev => ({ ...prev, [cacheKey]: 'ERROR' }));
            }
        } catch (error) {
            console.error('Failed to load Keepa chart:', error);
            setKeepaChartCache(prev => ({ ...prev, [cacheKey]: 'ERROR' }));
        } finally {
            setKeepaChartLoading(false);
        }
    };

    const calcProfit = () => {
        if (!deal.supplier_name) {
            return 0;
        } else {
            return metrics.profit;
        }
    };

    const calcCostPriceColor = () => {
        switch (deal.reverse_scan_status) {
            case 'failed':
                return "bg-red-50 border border-red-200";
            case 'no_match':
                return "bg-orange-50 border border-orange-200";
            case 'not_scanned':
                return "bg-gray-100 border-gray-300"
            default:
                if (supplierLogo === "checking.gif") {
                    return "bg-blue-50 border border-blue-200";
                }
                return "bg-yellow-50 border border-yellow-200"
        }
    };

    const amazonDomain = authService.getAmazonMarketplace();
    const currency = authService.getAmazonCurrency();
    const metrics = calculateMetrics();
    const data = deal.marketplace_data || {};
    const profit = calcProfit();
    const costPriceColor = calcCostPriceColor();

    const getQuickLinks = () => {
        const links = [];

        const marketplaceUrl =
            deal.marketplace === 'amazon' && data.asin
                ? `https://www.amazon${amazonDomain}/dp/${data.asin}`
                : deal.marketplace === 'walmart' && data.item_id
                    ? `https://www.walmart.com/ip/${data.item_id}`
                    : deal.marketplace === 'ebay' && data.ebay_search_link
                        ? data.ebay_search_link
                        : null;

        if (openUrl && marketplaceUrl) {
            links.push({
                label: hasMultipleSources ? `Choose from ${totalSources} Suppliers` : 'Open Both Links',
                action: () => {
                    if (hasMultipleSources) {
                        setShowMultiSourcePopup(true);
                    } else {
                        if (openUrl) {
                            window.open(openUrl, '_blank');
                            const secondWindow = window.open(marketplaceUrl, '_blank');
                            if (!secondWindow || secondWindow.closed || typeof secondWindow.closed === 'undefined') {
                                setShowPopupBlockedModal(true);
                            }
                        }
                    }
                },
                icon: hasMultipleSources ? '🛒' : '🔗'
            });
        }

        if (deal.marketplace === 'amazon') {
            if (data.asin) {
                links.push({
                    label: 'Hover to see Keepa chart!',
                    url: `https://keepa.com/#!product/${{ '.com': '1', '.co.uk': '2', '.ca': '6', '.com.mx': '8' }[amazonDomain] || '1'}-${data.asin}`,
                    icon: '📊',
                    showChart: true
                });
            }
        }

        return links;
    };

    const quickLinks = getQuickLinks();

    return (
        <div className="border border-gray-200 rounded-lg hover:shadow-lg transition bg-white overflow-visible">
            {deal.is_new && (
                <div className="absolute -mt-3.5 z-10">
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow-lg">
                        NEW
                    </span>
                </div>
            )}

            {showFeeSettings && (
                <FeeSettings
                    marketplace={deal.marketplace}
                    onClose={(newSettings) => {
                        if (newSettings) {
                            setFeeSettings(newSettings);
                            if (onSettingsChange) {
                                onSettingsChange();
                            }
                        }
                        setShowFeeSettings(false);
                    }}
                />
            )}

            {showKeepaSettings && (
                <KeepaSettingsModal
                    onClose={() => setShowKeepaSettings(false)}
                    onSave={(newSettings) => {
                        localStorage.setItem('keepa_chart_settings', JSON.stringify(newSettings));
                        setKeepaChartCache({});
                    }}
                />
            )}

            <div className="p-3 overflow-visible">
                <div className="flex gap-3 overflow-visible">
                    {/* Images Section - keeping it brief, same structure as before */}
                    <div className="flex flex-col gap-2 min-w-[90px]">
                        {/* BUY Section */}
                        <div className="flex flex-col gap-0.5">
                            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide text-center">Buy from</div>

                            {/* Buy From Image */}
                            {deal.reverse_scan_status === "not_scanned" ? (
                                <button
                                    onClick={handleRevealClick}
                                    className="relative group cursor-pointer"
                                    disabled={triggeringReverseScan}
                                    title={deal.upc && String(deal.upc).trim().replace(/\s/g, '').match(/^\d+$/) ? "Click to find supplier" : "No UPC - click for Universal Reverse Scan"}
                                >
                                    <img
                                        src={deal.upc && String(deal.upc).trim().replace(/\s/g, '').match(/^\d+$/) ? (deal.img || '/reveal.png') : '/noupc.png'}
                                        alt="Source"
                                        className="w-20 h-20 object-contain rounded border-2 border-yellow-400 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-yellow-400 relative bg-white m-auto"
                                        onError={(e) => e.target.src = '/reveal.png'}
                                    />
                                </button>
                            ) : deal.reverse_scan_status === "failed" ? (
                                <div className="relative group">
                                    <img
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                            const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                            const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                            const EXTidentifier = deal.marketplace === 'amazon' ? (
                                                deal?.marketplace_data?.asin
                                            ) : (
                                                deal.marketplace === 'walmart' ? (
                                                    deal?.marketplace_data?.item_id
                                                ) : (
                                                    deal.marketplace === 'ebay' ? (
                                                        deal?.marketplace_data?.upc
                                                    ) : ''
                                                )
                                            );
                                            if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                                window.chrome.runtime.sendMessage(EXT_ID, {
                                                    type: 'MSP_SEARCH_INTENT',
                                                    marketplace: deal.marketplace,
                                                    identifier: EXTidentifier,
                                                    title: deal.title,
                                                    deal,
                                                });
                                            }
                                            if (!title && upc) {
                                                window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                            }
                                            if (title) {
                                                window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                            }
                                        }}
                                        src={deal.img || '/checking.gif'}
                                        alt="Source"
                                        className="w-20 h-20 object-contain cursor-pointer rounded border-2 border-red-500 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl relative bg-white m-auto opacity-50"
                                        onError={(e) => e.target.src = '/checking.gif'}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold border-2 border-white shadow-lg">
                                            ✕
                                        </div>
                                    </div>
                                </div>
                            ) : deal.reverse_scan_status === "no_match" ? (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                        const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                        const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                        const EXTidentifier = deal.marketplace === 'amazon' ? (
                                            deal?.marketplace_data?.asin
                                        ) : (
                                            deal.marketplace === 'walmart' ? (
                                                deal?.marketplace_data?.item_id
                                            ) : (
                                                deal.marketplace === 'ebay' ? (
                                                    deal?.marketplace_data?.upc
                                                ) : ''
                                            )
                                        );
                                        if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                            window.chrome.runtime.sendMessage(EXT_ID, {
                                                type: 'MSP_SEARCH_INTENT',
                                                marketplace: deal.marketplace,
                                                identifier: EXTidentifier,
                                                title: deal.title,
                                                deal,
                                            });
                                        }
                                        if (!title & upc) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                        if (title) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                    }}
                                    className="relative group cursor-pointer"
                                >
                                    <img
                                        src={deal.img || '/checking.gif'}
                                        alt="Source"
                                        className="w-20 h-20 object-contain rounded border-2 border-orange-400 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-orange-500 relative bg-white m-auto opacity-50"
                                        onError={(e) => e.target.src = '/checking.gif'}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-orange-500 text-white px-2 py-1 rounded font-bold border-2 border-white shadow-lg text-[10px]">
                                            NO MATCH
                                        </div>
                                    </div>
                                </button>
                            ) : deal.reverse_scan_status === 'stopped' ? (
                                <button
                                    onClick={handleRevealClick}
                                    className="relative group cursor-pointer"
                                    disabled={triggeringReverseScan}
                                    title="Search was paused - click to reveal again"
                                >
                                    <img
                                        src={deal.img || '/reveal.png'}
                                        alt="Source"
                                        className="w-20 h-20 object-contain rounded border-2 border-gray-400 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-gray-500 relative bg-white m-auto opacity-60"
                                        onError={(e) => e.target.src = '/reveal.png'}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-gray-500 text-white px-2 py-1 rounded font-bold border-2 border-white shadow-lg text-[10px]">
                                            ⏸ PAUSED
                                        </div>
                                    </div>
                                </button>
                            ) : supplierLogo === 'checking.gif' ? (
                                <button
                                    onClick={() => setShowSearchingPopup(true)}
                                    className="relative group cursor-pointer"
                                    title="Click to use Universal Reverse Scan while waiting"
                                >
                                    <img
                                        src={deal.img || '/checking.gif'}
                                        alt="Source"
                                        className="w-20 h-20 object-contain rounded border-2 border-blue-300 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-blue-400 relative bg-white m-auto"
                                        onError={(e) => e.target.src = '/checking.gif'}
                                    />
                                </button>
                            ) : (
                                <div
                                    className="relative group cursor-pointer"
                                    onClick={(e) => {
                                        if (hasMultipleSources) {
                                            e.preventDefault();
                                            setShowMultiSourcePopup(true);
                                        } else {
                                            if (openUrl) {
                                                window.open(openUrl, '_blank');
                                            }
                                        }
                                    }}
                                >
                                    <img
                                        src={deal.img || supplierLogo || '/checking.gif'}
                                        alt="Source"
                                        className="w-20 h-20 object-contain rounded border-2 border-gray-200 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-yellow-400 relative bg-white m-auto"
                                        onError={(e) => e.target.src = supplierLogo || '/noimage.png'}
                                    />
                                    {hasMultipleSources && (
                                        <div className="absolute -top-1 -right-1 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold border-2 border-white shadow-lg">
                                            {totalSources}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Supplier Logo/Text */}
                            {deal.reverse_scan_status === "failed" ? (
                                <div className="flex items-center justify-center gap-1">
                                    <span className="text-[11px] font-bold text-red-600">SCAN FAILED</span>
                                </div>
                            ) : deal.reverse_scan_status === "no_match" ? (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                        const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                        const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                        const EXTidentifier = deal.marketplace === 'amazon' ? (
                                            deal?.marketplace_data?.asin
                                        ) : (
                                            deal.marketplace === 'walmart' ? (
                                                deal?.marketplace_data?.item_id
                                            ) : (
                                                deal.marketplace === 'ebay' ? (
                                                    deal?.marketplace_data?.upc
                                                ) : ''
                                            )
                                        );
                                        if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                            window.chrome.runtime.sendMessage(EXT_ID, {
                                                type: 'MSP_SEARCH_INTENT',
                                                marketplace: deal.marketplace,
                                                identifier: EXTidentifier,
                                                title: deal.title,
                                                deal,
                                            });
                                        }
                                        if (!title && upc) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                        if (title) {
                                            window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                        }
                                    }}
                                    className="flex items-center justify-center gap-1 hover:bg-orange-100 rounded transition-all cursor-pointer mx-auto px-2 py-1"
                                >
                                    <span className="text-[11px] font-bold text-orange-600">Click to search</span>
                                </button>
                            ) : supplierLogo !== "checking.gif" ? (
                                <div
                                    className="flex items-center justify-center gap-1 hover:opacity-70 transition-opacity cursor-pointer"
                                    onClick={() => hasMultipleSources ? setShowMultiSourcePopup(true) : (openUrl && window.open(openUrl, '_blank'))}
                                >
                                    <img
                                        src={supplierLogo}
                                        alt={deal.supplier_name}
                                        className="w-4 h-4 object-contain flex-shrink-0"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <div className="text-[12px] font-semibold text-gray-700 truncate">
                                        {hasMultipleSources ? `${totalSources} Suppliers` : (deal.supplier_name ? deal.supplier_name.charAt(0).toUpperCase() + deal.supplier_name.slice(1) : "N/A")}
                                    </div>
                                </div>
                            ) : deal.reverse_scan_status === "not_scanned" ? (
                                <button
                                    onClick={handleRevealClick}
                                    disabled={triggeringReverseScan}
                                    className="flex items-center justify-center gap-1 hover:bg-yellow-200 rounded transition-all cursor-pointer mx-auto"
                                    title={deal.upc && String(deal.upc).trim().match(/^\d+$/) ? "Click to find supplier" : "No UPC data - click to search manually on Google"}
                                >
                                    <span className="text-[11px] font-bold text-yellow-800">FIND SUPPLIER</span>
                                </button>
                            ) : deal.reverse_scan_status === 'stopped' ? (
                                <button
                                    onClick={handleRevealClick}
                                    disabled={triggeringReverseScan}
                                    className="flex items-center justify-center gap-1 hover:bg-gray-200 rounded transition-all cursor-pointer mx-auto px-2 py-0.5"
                                    title="Paused - click to reveal supplier again"
                                >
                                    <span className="text-[11px] font-bold text-gray-500">⏸ PAUSED</span>
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowSearchingPopup(true)}
                                    className="flex items-center justify-center gap-1 hover:bg-blue-100 rounded transition-all cursor-pointer mx-auto px-1 py-0.5"
                                    title="Click to use Universal Reverse Scan while waiting"
                                >
                                    <RefreshCw className="animate-spin [animation-duration:2s] w-3 h-3 object-contain flex-shrink-0 text-blue-600" />
                                    <div className="text-[12px] font-semibold text-blue-600">Searching</div>
                                </button>
                            )}
                        </div>

                        <div className="border-t border-gray-300 my-0.5"></div>

                        {/* SELL Section - similar structure */}
                        <div className="flex flex-col gap-0.5">
                            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide text-center">Sell on</div>
                            <a href={
                                deal.marketplace === 'amazon' && data.asin ? `https://www.amazon${amazonDomain}/dp/${data.asin}` :
                                    deal.marketplace === 'walmart' && data.item_id ? `https://www.walmart.com/ip/${data.item_id}` :
                                        deal.marketplace === 'ebay' && data.ebay_search_link ? data.ebay_search_link : '#'
                            } target="_blank" rel="noopener noreferrer" className="relative group cursor-pointer">
                                <img src={(deal.marketplace === 'amazon' ? data.image : deal.marketplace === 'walmart' ? data.image : deal.marketplace === 'ebay' ? data.image : null)}
                                    alt="Marketplace"
                                    className="w-20 h-20 object-contain rounded border-2 border-gray-200 transition-all duration-300 group-hover:scale-[2.5] group-hover:z-[100] group-hover:shadow-2xl group-hover:border-green-400 relative bg-white m-auto"
                                    onError={(e) => e.target.src = '/noimage.png'} />
                            </a>
                            <a href={
                                deal.marketplace === 'amazon' && data.asin ? `https://www.amazon${amazonDomain}/dp/${data.asin}` :
                                    deal.marketplace === 'walmart' && data.item_id ? `https://www.walmart.com/ip/${data.item_id}` :
                                        deal.marketplace === 'ebay' && data.ebay_search_link ? data.ebay_search_link : '#'
                            } target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 hover:opacity-70 transition-opacity">
                                {marketplaceLogo && (
                                    <img src={marketplaceLogo} alt={deal.marketplace} className="w-4 h-4 object-contain flex-shrink-0"
                                        onError={(e) => e.target.style.display = 'none'} />
                                )}
                                <div className="text-[12px] font-semibold text-gray-700 capitalize">{deal.marketplace}</div>
                            </a>
                        </div>
                    </div>

                    {/* Main Info Section */}
                    <div className="flex-1 min-w-0">
                        <div className="mb-2 flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0 flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                        <div className="pt-0.5">
                                            {deal.is_universal ? (
                                                <UniversalFavoriteButton deal={deal} onFavoriteChange={() => { }} />
                                            ) : (
                                                <FavoriteButton deal={deal} onSettingsChange={onSettingsChange} />
                                            )}
                                        </div>
                                        {deal.found_in_stores_count > 1 && (
                                            <span className="relative group/multistore inline-flex items-center gap-1 bg-blue-100 text-blue-800 border border-blue-300 px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 cursor-help">
                                                🏪 {deal.found_in_stores_count} STOREFRONTS
                                                {deal.found_in_stores?.length > 0 && (
                                                    <div className="absolute left-0 top-full mt-1 hidden group-hover/multistore:block z-50 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                                        Found in: {deal.found_in_stores.join(', ')}
                                                    </div>
                                                )}
                                            </span>
                                        )}
                                        <h3 className="font-semibold text-sm text-gray-800 line-clamp-2 flex-1">
                                            {cleanedTitle}
                                        </h3>
                                    </div>
                                    {sellerNote && (
                                        <span className={`text-xs px-2 py-0.5 rounded ${sellerNote.isGood
                                            ? (sellerNote.type === 'walmart' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700')
                                            : (sellerNote.type === 'walmart' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700')
                                            }`}>
                                            {sellerNote.note}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {marketplaceLogo && (
                                <img src={marketplaceLogo} alt={deal.marketplace}
                                    className="w-8 h-8 object-contain flex-shrink-0 opacity-20 hover:opacity-100 transition-opacity"
                                    onError={(e) => e.target.style.display = 'none'} />
                            )}
                        </div>

                        {/* Compact Data Grid */}
                        <div className="grid grid-cols-[1fr_auto_1fr] gap-6 mb-2">
                            {/* Left Block */}
                            <div className="grid grid-cols-2 gap-2">
                                <div className={`rounded p-2 border ${costPriceColor}`}>
                                    <div className="text-gray-600 text-[10px] font-semibold text-center flex items-center justify-center gap-1">
                                        <span>Cost Price</span>
                                        <PackSizeButton packSize={packSize} onPackSizeChange={setPackSize} />
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowCashbackModal(true);
                                            }}
                                            className="text-orange-600 hover:text-orange-800 transition flex items-center"
                                            title="Find cashback & discounted gift cards"
                                        >
                                            <span className="text-xs">💸</span>
                                        </button>
                                        {couponAmount > 0 && <span className="text-green-600">(-{couponAmount}%)</span>}
                                    </div>
                                    <div className="font-bold text-m text-gray-800 text-center flex items-center justify-center gap-1">
                                        {deal.reverse_scan_status === "failed" ? (
                                            <div className="flex items-center justify-center gap-1">
                                                <span className="text-[11px] font-bold text-red-600">SCAN FAILED</span>
                                            </div>
                                        ) : deal.reverse_scan_status === "no_match" ? (
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const upc = deal.marketplace_data?.upc || deal.marketplace_data?.ean || deal.marketplace_data?.gtin || deal.upc || '';
                                                    const title = deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                                    const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                                    const EXTidentifier = deal.marketplace === 'amazon' ? (
                                                        deal?.marketplace_data?.asin
                                                    ) : (
                                                        deal.marketplace === 'walmart' ? (
                                                            deal?.marketplace_data?.item_id
                                                        ) : (
                                                            deal.marketplace === 'ebay' ? (
                                                                deal?.marketplace_data?.upc
                                                            ) : ''
                                                        )
                                                    );
                                                    if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                                        window.chrome.runtime.sendMessage(EXT_ID, {
                                                            type: 'MSP_SEARCH_INTENT',
                                                            marketplace: deal.marketplace,
                                                            identifier: EXTidentifier,
                                                            title: deal.title,
                                                            deal,
                                                        });
                                                    }
                                                    if (!title && upc) {
                                                        window.open(`https://www.google.com/search?q=${encodeURIComponent(upc)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                                    }
                                                    if (title) {
                                                        window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                                    }
                                                }}
                                                className="flex items-center justify-center gap-1 hover:bg-orange-100 rounded transition-all cursor-pointer mx-auto px-2 py-1"
                                            >
                                                <span className="text-[11px] font-bold text-orange-600">Click to search</span>
                                            </button>
                                        ) : supplierLogo !== "checking.gif" ? (
                                            <>
                                                ${((deal.source_price || 0) * (1 - (couponAmount / 100)) * packSize).toFixed(2)}
                                                {!hasMultipleSources && <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const overrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');

                                                        // Get original price (stored or current if never edited)
                                                        const originalPrice = overrides[deal.id]?.original_source_price ?? deal.source_price;
                                                        const currentPrice = deal.source_price;

                                                        const promptText = originalPrice?.toFixed(2) !== currentPrice?.toFixed(2)
                                                            ? `Enter new cost price (originally $${originalPrice?.toFixed(2)}):`
                                                            : 'Enter new cost price:';

                                                        const newPrice = prompt(promptText, currentPrice?.toFixed(2));
                                                        if (newPrice !== null && !isNaN(parseFloat(newPrice))) {
                                                            // Save original on first edit only
                                                            if (!overrides[deal.id]) {
                                                                overrides[deal.id] = { original_source_price: deal.source_price };
                                                            }

                                                            // Update the deal object
                                                            deal.source_price = parseFloat(newPrice);

                                                            // Save the new price
                                                            overrides[deal.id].source_price = parseFloat(newPrice);
                                                            localStorage.setItem('deal_price_overrides', JSON.stringify(overrides));

                                                            if (onSettingsChange) onSettingsChange();
                                                        }
                                                    }}
                                                    className="text-gray-500 hover:text-gray-700 cursor-pointer text-[12px]"
                                                    title="Edit cost price"
                                                >
                                                    ✏️
                                                </button>}
                                                {hasMultipleSources && <span className="text-blue-600 text-[11px]">(Lowest)</span>}
                                            </>
                                        ) : deal.reverse_scan_status === "not_scanned" ? (
                                            <button
                                                onClick={handleRevealClick}
                                                disabled={triggeringReverseScan}
                                                className="flex items-center justify-center gap-1 hover:bg-yellow-200 rounded transition-all cursor-pointer mx-auto"
                                                title={deal.upc && String(deal.upc).trim().match(/^\d+$/) ? "Click to find supplier" : "No UPC data - click to search manually on Google"}
                                            >
                                                <span className="text-[11px] font-bold text-yellow-800">FIND SUPPLIER</span>
                                            </button>
                                        ) : deal.reverse_scan_status === 'stopped' ? (
                                            <button
                                                onClick={handleRevealClick}
                                                disabled={triggeringReverseScan}
                                                className="flex items-center justify-center gap-1 hover:bg-gray-200 rounded transition-all cursor-pointer mx-auto"
                                                title="Paused - click to reveal supplier again"
                                            >
                                                <span className="text-[11px] font-bold text-gray-500">⏸ PAUSED</span>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => setShowSearchingPopup(true)}
                                                className="flex items-center justify-center gap-1 hover:bg-blue-100 rounded transition-all cursor-pointer mx-auto"
                                                title="Click to use Universal Reverse Scan while waiting"
                                            >
                                                <RefreshCw className="animate-spin [animation-duration:2s] w-3 h-3 object-contain flex-shrink-0 text-blue-600" />
                                                <div className="text-[12px] font-semibold text-blue-600">Searching</div>
                                            </button>
                                        )}

                                    </div>
                                </div>
                                <div className="bg-green-50 rounded p-1 border border-green-200 relative group">
                                    <div className="text-gray-600 text-[10px] font-semibold text-center flex items-center justify-center gap-1">
                                        Sell Price{window.location.pathname.includes('a2a') ? " (90d avg)" : ""}
                                        {deal.marketplace === 'amazon' && data.used_bb && (
                                            <span className="text-green-600 cursor-help" title="This is the Buy Box price">(Buy Box)</span>
                                        )}
                                    </div>
                                    <div className="text-center flex items-center justify-center gap-1">
                                        <div className="font-bold text-m text-green-800">{currency}{metrics.sellPrice?.toFixed(2)}</div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const data = deal.marketplace_data || {};
                                                const overrides = JSON.parse(localStorage.getItem('deal_price_overrides') || '{}');

                                                // Get current sell price from data
                                                const currentSellPrice = (deal.marketplace === 'amazon' ? data.az_price :
                                                    deal.marketplace === 'walmart' ? data.price_value :
                                                        deal.marketplace === 'ebay' ? data.lowest_price : 0);

                                                // Get original price (stored or current if never edited)
                                                const originalSellPrice = overrides[deal.id]?.original_sell_price ?? currentSellPrice;

                                                const promptText = originalSellPrice?.toFixed(2) !== currentSellPrice?.toFixed(2)
                                                    ? `Enter new sell price (originally $${originalSellPrice?.toFixed(2)}):`
                                                    : 'Enter new sell price:';

                                                const newPrice = prompt(promptText, currentSellPrice?.toFixed(2));
                                                if (newPrice !== null && !isNaN(parseFloat(newPrice))) {
                                                    const parsedPrice = parseFloat(newPrice);

                                                    // Save original on first edit only
                                                    if (!overrides[deal.id]) {
                                                        overrides[deal.id] = { original_sell_price: currentSellPrice };
                                                    }

                                                    // Update the deal object
                                                    if (deal.marketplace === 'amazon') {
                                                        data.az_price = parsedPrice;
                                                    } else if (deal.marketplace === 'walmart') {
                                                        data.price_value = parsedPrice;
                                                    } else if (deal.marketplace === 'ebay') {
                                                        data.lowest_price = parsedPrice;
                                                    }

                                                    // Save the new price
                                                    overrides[deal.id].sell_price = parsedPrice;
                                                    localStorage.setItem('deal_price_overrides', JSON.stringify(overrides));

                                                    if (onSettingsChange) onSettingsChange();
                                                }
                                            }}
                                            className="text-gray-500 hover:text-gray-700 cursor-pointer text-[12px]"
                                            title="Edit sell price"
                                        >
                                            ✏️
                                        </button>
                                    </div>

                                    {deal.storefront_price && (
                                        <div className="text-[10px] text-center mt-0.5">
                                            <span className="text-gray-500">Storefront: ${deal.storefront_price.toFixed(2)}</span>
                                            {Math.abs(deal.storefront_price - metrics.sellPrice) < 0.01
                                                ? <span className="text-green-600 ml-1">✓</span>
                                                : <span className="text-orange-500 ml-1" title={`Storefront price differs from current sell price`}>⚠️</span>
                                            }
                                        </div>
                                    )}

                                    {deal.marketplace === 'amazon' && (
                                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-white border-2 border-green-300 rounded-lg shadow-xl p-3 min-w-[160px]">
                                            <div className="text-xs font-semibold text-gray-700 mb-2 text-center border-b pb-1">Average Price</div>
                                            <div className="space-y-1.5">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-xs text-gray-600 font-semibold">30 Days:</span>
                                                    <span className="text-sm font-bold text-green-700">${(data.az_price_avg30 || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-xs text-gray-600 font-semibold">90 Days:</span>
                                                    <span className="text-sm font-bold text-green-700">${(data.az_price_avg90 || 0).toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 -mt-1">
                                                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-green-300"></div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Profit, ROI, Total Fees, Gated Status boxes continue here... */}
                                <div className={`rounded p-1 border ${profit > 0 ? 'bg-green-100 border-green-300' : 'bg-gray-100 border-gray-300'}`}>
                                    <div className="text-gray-600 text-[10px] font-semibold text-center">Profit</div>
                                    <div className={`font-bold text-base text-center ${profit > 0 ? 'text-green-900' : 'text-gray-700'}`}>
                                        ${profit?.toFixed(2)}
                                    </div>
                                </div>

                                <div className={`rounded p-1 border ${metrics.roi > 0 ? 'bg-purple-100 border-purple-300' : 'bg-gray-100 border-gray-300'}`}>
                                    <div className="text-gray-600 text-[10px] font-semibold text-center">ROI</div>
                                    <div className={`font-bold text-base text-center ${metrics.roi > 0 ? 'text-purple-900' : 'text-gray-700'}`}>
                                        {metrics.roi?.toFixed(1)}%
                                    </div>
                                </div>

                                <div className="bg-red-50 rounded p-1 border border-red-200 relative group">
                                    <div className="text-gray-600 text-[10px] font-semibold text-center flex items-center justify-center gap-1">
                                        {deal.marketplace === 'amazon' && onFulfillmentChange && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onFulfillmentChange(fulfillmentType === 'fba' ? 'fbm' : 'fba'); }}
                                                className={`-ml-8 px-1 rounded text-[9px] font-bold border transition-colors ${fulfillmentType === 'fba' ? 'bg-orange-100 border-orange-400 text-orange-700' : 'bg-gray-100 border-gray-400 text-gray-600'}`}
                                            >
                                                {fulfillmentType === 'fba' ? 'FBA' : 'FBM'}
                                            </button>
                                        )}
                                        {deal.marketplace === 'walmart' && onFulfillmentChange && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onFulfillmentChange(fulfillmentType === 'wfs' ? 'sf' : 'wfs'); }}
                                                className={`-ml-8 px-1 rounded text-[9px] font-bold border transition-colors ${fulfillmentType === 'wfs' ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-gray-100 border-gray-400 text-gray-600'}`}
                                            >
                                                {fulfillmentType === 'wfs' ? 'WFS' : 'SF'}
                                            </button>
                                        )}
                                        Total Fees
                                        {deal.marketplace === 'walmart' && metrics.missingData && (metrics.missingData.category || (fulfillmentType === 'wfs' && (metrics.missingData.weight || metrics.missingData.dimensions))) && (
                                            <span className="text-orange-600 text-s -mt-0.5" title="Missing data - hover to see details">⚠️</span>
                                        )}
                                    </div>
                                    <div className="font-bold text-base text-red-800 text-center">${metrics.totalFees?.toFixed(2)}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowFeeSettings(true);
                                            }}
                                            className="text-gray-500 hover:text-gray-700 cursor-pointer text-s ml-1"
                                            title="Configure fee settings"
                                            style={{ border: '1px solid rgba(0,0,0,0.15)', borderRadius: "20px" }}
                                        >
                                            ⚙️
                                        </button>
                                    </div>

                                    {/* Fee Breakdown Hover Popup */}
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-white border-2 border-red-300 rounded-lg shadow-xl p-3 min-w-[200px]">
                                        <div className="text-xs font-semibold text-gray-700 mb-2 text-center border-b pb-1">Fee Breakdown</div>
                                        {deal.marketplace === 'walmart' && metrics.missingData && (metrics.missingData.category || (fulfillmentType === 'wfs' && (metrics.missingData.weight || metrics.missingData.dimensions))) && (
                                            <div className="mb-2 pb-2 border-b border-orange-200">
                                                {fulfillmentType === 'wfs' && metrics.missingData.weight && (
                                                    <div className="flex items-center gap-1 text-orange-600 text-xs mb-1">
                                                        <span>⚠️</span>
                                                        <span>Weight missing (WFS)</span>
                                                    </div>
                                                )}
                                                {fulfillmentType === 'wfs' && metrics.missingData.dimensions && (
                                                    <div className="flex items-center gap-1 text-orange-600 text-xs mb-1">
                                                        <span>⚠️</span>
                                                        <span>Dimensions missing (WFS)</span>
                                                    </div>
                                                )}
                                                {metrics.missingData.category && (
                                                    <div className="flex items-center gap-1 text-orange-600 text-xs mb-1">
                                                        <span>⚠️</span>
                                                        <span>Category missing (Referral)</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="space-y-1.5 text-xs">
                                            {deal.marketplace === 'amazon' && (
                                                <>
                                                    {fulfillmentType === 'fba' && metrics.breakdown.fbaFee > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">FBA Fee:</span>
                                                            <span className="font-semibold">${metrics.breakdown.fbaFee?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Referral Fee:</span>
                                                        <span className="font-semibold">${metrics.breakdown.refFee?.toFixed(2)}</span>
                                                    </div>
                                                    {metrics.breakdown.placementFees > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Placement:</span>
                                                            <span className="font-semibold">${metrics.breakdown.placementFees?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {metrics.breakdown.inboundShipping > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Inbound Ship:</span>
                                                            <span className="font-semibold">${metrics.breakdown.inboundShipping?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {deal.marketplace === 'walmart' && (
                                                <>
                                                    {fulfillmentType === 'wfs' && metrics.breakdown.wfsFee > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">WFS Fee{(metrics.missingData.weight || metrics.missingData.dimensions) && " ($3.45 Default)"}:</span>
                                                            <span className="font-semibold">${metrics.breakdown.wfsFee?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Referral Fee:</span>
                                                        <span className="font-semibold">${metrics.breakdown.refFee?.toFixed(2)}</span>
                                                    </div>
                                                    {metrics.breakdown.inboundShipping > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Inbound Ship:</span>
                                                            <span className="font-semibold">${metrics.breakdown.inboundShipping?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {deal.marketplace === 'ebay' && (
                                                <>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Final Value Fee (Most Ctg. 13.6%):</span>
                                                        <span className="font-semibold">${metrics.breakdown.finalValueFee?.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Per-Order Fee:</span>
                                                        <span className="font-semibold">${metrics.breakdown.perOrderFee?.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Payment Processing (2.35% + $0.30):</span>
                                                        <span className="font-semibold">${metrics.breakdown.paymentProcessingFee?.toFixed(2)}</span>
                                                    </div>
                                                    {metrics.breakdown.shippingCharge > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Shipping Charge (Revenue):</span>
                                                            <span className="font-semibold text-green-600">+${metrics.breakdown.shippingCharge?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {metrics.breakdown.shippingCost > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Shipping Cost (Expense):</span>
                                                            <span className="font-semibold">${metrics.breakdown.shippingCost?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {metrics.breakdown.salesTax > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Sales Tax:</span>
                                                            <span className="font-semibold">${metrics.breakdown.salesTax?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {metrics.breakdown.packagingCost > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Packaging:</span>
                                                            <span className="font-semibold">${metrics.breakdown.packagingCost?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {metrics.breakdown.otherCosts > 0 && (
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Other:</span>
                                                            <span className="font-semibold">${metrics.breakdown.otherCosts?.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {metrics.breakdown.salesTax > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Sales Tax:</span>
                                                    <span className="font-semibold">${metrics.breakdown.salesTax?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {metrics.breakdown.otherCosts > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Other:</span>
                                                    <span className="font-semibold">${metrics.breakdown.otherCosts?.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {metrics.breakdown.fulfillmentCost > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Fulfillment Cost:</span>
                                                    <span className="font-semibold">${metrics.breakdown.fulfillmentCost?.toFixed(2)}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                                            <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-red-300"></div>
                                        </div>
                                    </div>
                                </div>

                                {deal.marketplace !== "ebay" && (
                                    <div className={`rounded p-1 border ${!amazonLinked && deal.marketplace === 'amazon'
                                        ? 'bg-yellow-50 border-yellow-300'
                                        : deal.gated_status === 'gated' ? 'bg-red-50 border-red-400' :
                                            deal.gated_status === 'approval' ? 'bg-orange-50 border-orange-400' :
                                                deal.gated_status === 'ungated' ? 'bg-green-50 border-green-400' :
                                                    'bg-gray-50 border-gray-200'
                                        }`}>
                                        <div className="text-gray-600 text-[10px] font-semibold text-center">Gated Status</div>
                                        <div className="flex items-center justify-center gap-1">
                                            {deal.marketplace === 'amazon' && !amazonLinked ? (
                                                <button
                                                    onClick={async () => {
                                                        window.open(
                                                            `https://sellercentral.amazon${authService.getAmazonMarketplace()}/apps/authorize/consent?application_id=amzn1.sp.solution.18aecca2-d85f-492c-b0ba-d121066f7ef8&state=${encodeURIComponent(authService.credentials?.email || '')}`,
                                                            "_blank"
                                                        );
                                                    }}
                                                    style={{ margin: "auto", border: "none", background: "transparent", cursor: "pointer" }}
                                                >
                                                    <img
                                                        src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_101x46.png"
                                                        style={{ height: 25 }}
                                                        alt="Link Amazon Account"
                                                    />
                                                </button>
                                            ) : deal.marketplace === "walmart" ? (
                                                <span className="text-gray-600 px-2 py-1 rounded text-xs font-semibold">Coming soon...</span>
                                            ) : (
                                                <>
                                                    {onRefreshGated && (
                                                        <button
                                                            onClick={onRefreshGated}
                                                            disabled={refreshing}
                                                            className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                            title="Refresh gated status"
                                                        >
                                                            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                                                        </button>
                                                    )}
                                                    {getGatedBadge(deal.gated_status, data.asin)}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Vertical Separator */}
                            <div className="flex items-center justify-center px-2">
                                <div className="w-px h-full bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                            </div>

                            {/* Right Block - Marketplace-Specific Data */}
                            {/* Similar structure to your original... keeping the Amazon/Walmart/eBay sections */}
                            <div className="grid grid-cols-2 gap-2">
                                {deal.marketplace === 'amazon' && (
                                    <>
                                        <div className="bg-indigo-50 rounded p-1 border border-indigo-200">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center mb-0.5">Brand @ Category</div>
                                            <div className="font-bold text-[12px] text-gray-800 text-center truncate overflow-hidden whitespace-nowrap" title={`${data.brand || 'N/A'} @ ${data.category || 'N/A'}`}>
                                                {data.brand || 'N/A'} @ {data.category || 'N/A'}
                                            </div>
                                        </div>
                                        {/* BSR, Offers, Variations, etc. continue here... */}
                                        <div className="bg-blue-50 rounded p-1 border border-blue-200 relative group">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center mb-0.5">BSR</div>
                                            <div className="text-center flex items-center justify-center gap-1">
                                                <div className="font-bold text-base text-blue-800">
                                                    #{(data.sales_rank || 0).toLocaleString()}
                                                </div>
                                                <div className="text-[12px] text-blue-700 font-semibold">
                                                    (Top {data.sales_rank_perc?.toFixed(1) || '0.0'}%)
                                                </div>
                                            </div>

                                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 bg-white border-2 border-blue-300 rounded-lg shadow-xl p-3 min-w-[160px]">
                                                <div className="text-xs font-semibold text-gray-700 mb-2 text-center border-b pb-1">Average BSR</div>
                                                <div className="space-y-1.5">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs text-gray-600 font-semibold">30 Days:</span>
                                                        <span className="text-sm font-bold text-blue-700">#{(data.bsr_avg30 || 0).toLocaleString()}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs text-gray-600 font-semibold">90 Days:</span>
                                                        <span className="text-sm font-bold text-blue-700">#{(data.bsr_avg90 || 0).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 -mt-1">
                                                    <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-blue-300"></div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 rounded p-1 border border-gray-200">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center">Offers</div>
                                            <div className="font-bold text-sm text-gray-800 text-center flex items-center justify-center gap-1">
                                                {data.total_offers_count || 0}
                                                <User size={12} className="text-gray-600" />
                                                {data.az_sells_it && (
                                                    <span className="cursor-help" title="Amazon is selling this product">
                                                        <img
                                                            src={MARKETPLACE_LOGOS.amazon}
                                                            alt="Amazon sells"
                                                            className="w-4 h-4 object-contain mt-0.5 ml-1"
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="bg-purple-50 rounded p-1 border border-purple-200">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center">Variations</div>
                                            <div className="text-center">
                                                <div className="font-bold text-sm text-purple-800">{data.variations}</div>
                                            </div>
                                        </div>
                                        <div className="bg-cyan-50 rounded p-1 border border-cyan-200">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center mb-0.5">BSR Drops (30d | 90d)</div>
                                            <div className="text-center">
                                                <div className="font-bold text-sm text-cyan-800">
                                                    {data.sales_rank_drops_30_days || 0} <span className="text-cyan-600">|</span> {data.sales_rank_drops_90_days || 0}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-teal-50 rounded p-1 border border-teal-200">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center mb-0.5">
                                                {data.keepa_monthly_sold > 0 ? 'Verified Monthly Sales' : 'Est. Monthly Sales'}
                                            </div>
                                            <div className="text-center flex items-center justify-center gap-1">
                                                {data.keepa_monthly_sold > 0 && (
                                                    <span
                                                        className="text-green-600 cursor-help text-sm"
                                                        title="Keepa monthly sold - verified by Amazon and variation specific"
                                                    >
                                                        ✓
                                                    </span>
                                                )}
                                                <div className="font-bold text-sm text-teal-800">
                                                    {data.keepa_monthly_sold > 0 ? `${data.keepa_monthly_sold}+` : (amazonDomain !== '.com' ? Math.round((data.estimate_sales_30_days || 0) / 6) : (data.estimate_sales_30_days || 0))}/mo
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {deal.marketplace === 'walmart' && (
                                    <>
                                        <div className="bg-gray-50 rounded p-1 border border-gray-200 flex flex-col justify-center">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center">Offers</div>
                                            <div className="flex items-center justify-center gap-1">
                                                <div className="font-bold text-sm text-gray-800">{data.offers || 0}</div>

                                                {/* Show storefront stock status if on storefront scan page */}
                                                {window.location.pathname.includes("storefront-scan") && deal.storefront_scanned_instock !== null && (
                                                    deal.storefront_scanned_instock ? (
                                                        <>
                                                            <span className="text-green-600 font-bold text-xs">✓</span>
                                                            <InfoTooltip text="The tracked storefront has this product in stock" style={{ color: "green", marginTop: -3 }} />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="text-red-600 font-bold text-xs">OOS!</span>
                                                            <InfoTooltip text="The tracked storefront doesn't currently have this product in stock" style={{ color: "red", marginTop: -3 }} />
                                                        </>
                                                    )
                                                )}

                                                {/* Show marketplace stock status if NOT on storefront scan page OR if storefront stock is null */}
                                                {(!window.location.pathname.includes("storefront-scan") || deal.storefront_scanned_instock === null) && (
                                                    !data.in_stock ? (
                                                        <>
                                                            <span className="text-red-600 font-bold text-xs">OOS!</span>
                                                            <InfoTooltip text="This item is OOS on the Walmart marketplace - be the only seller to sell it" style={{ color: "red", marginTop: -3 }} />
                                                        </>
                                                    ) : <User size={12} className="text-gray-600" />
                                                )}
                                                {data.is_walmart && (
                                                    <span className="cursor-help" title="Walmart is selling this product">
                                                        <img
                                                            src={MARKETPLACE_LOGOS.walmart}
                                                            alt="Walmart sells"
                                                            className="w-4 h-4 object-contain mt-0.5 ml-1"
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 rounded p-1 border border-gray-200 flex flex-col justify-center">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center">Reviews</div>
                                            <div className="font-bold text-sm text-gray-800 text-center">{(data.number_of_reviews || 0).toLocaleString()} (⭐ {(data.average_rating || 0).toFixed(1)})</div>
                                        </div>
                                        <div className="bg-gray-50 rounded p-1 border border-gray-200 row-span-3 col-span-2 flex flex-col">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center">Badges</div>

                                            <div className="flex flex-wrap justify-center items-center gap-2 mt-3">
                                                {data.badges && data.badges.length > 0 ? (
                                                    data.badges.map((badge) => {
                                                        const randomColor = `hsl(${Math.floor(Math.random() * 360)}, 70%, 40%)`;

                                                        return (
                                                            <div
                                                                key={badge.id}
                                                                className="text-[12.5px] font-semibold text-white px-2 py-0.5 rounded-full whitespace-nowrap"
                                                                style={{ backgroundColor: randomColor }}
                                                            >
                                                                {badge.text}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="text-gray-400 text-xs font-semibold">No Badges</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {deal.marketplace === 'ebay' && (
                                    data.total_offers != -1 ? <>
                                        <div className="bg-gray-50 rounded p-1 border border-gray-200 flex flex-col justify-center">
                                            <div className="text-gray-600 text-[10px] font-semibold text-center">Offers</div>
                                            <div className="flex items-center justify-center gap-1">
                                                <div className="font-bold text-sm text-gray-800">{data.total_offers || 0}</div>
                                                <User size={12} className="text-gray-600" />
                                            </div>
                                        </div>
                                        <div className="bg-purple-50 rounded p-1 border border-purple-200 flex flex-col justify-center">
                                            {data.estimated_monthly_sales === -1 ? (
                                                <div className="text-purple-700 text-[11px] font-semibold text-center leading-relaxed">
                                                    For Estimated Sales, use our <a href="https://chromewebstore.google.com/detail/multisellerpro/elebkaoefjdagkbfienaoonkgifogkob" target="_blank" rel="noopener noreferrer" className="underline font-bold hover:text-purple-900">Chrome Extension</a> on <a href={data.ebay_search_link} target="_blank" rel="noopener noreferrer" className="underline font-bold hover:text-purple-900">the listing</a>!
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="text-gray-600 text-[10px] font-semibold text-center">Est. Monthly Sales</div>
                                                    <div className="font-bold text-sm text-purple-800 text-center">{data.estimated_monthly_sales || 0}</div>
                                                </>
                                            )}
                                        </div>
                                        <div className="rounded p-1 row-span-3 col-span-2 flex flex-col">
                                        </div>
                                    </> :
                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 col-span-2">
                                            <div className="text-gray-600 text-xs font-semibold text-center mb-2">
                                                Seller's Link
                                            </div>
                                            <div className="flex flex-col items-center gap-3">
                                                <p className="text-sm text-gray-700 text-center max-w-md">
                                                    Open it and use our <span className="font-semibold text-gray-900">FREE Extension</span> for more data!
                                                </p>
                                                <a
                                                    href={`https://www.ebay.com/itm/${deal.product_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-blue-600 hover:text-blue-800 underline break-all text-center px-2 transition-colors"
                                                >
                                                    https://www.ebay.com/itm/{deal.product_id}
                                                </a>
                                            </div>
                                        </div>
                                )}
                            </div>
                        </div>

                        {/* ASIN/Item ID, UPC, Quick Links, and Hide Button */}
                        <div className="flex items-center justify-between gap-4 mt-3 mb-1">
                            <div className="text-xs text-gray-500 flex items-center gap-4">
                                {quickLinks.find(link => link.action) && (
                                    <button
                                        onClick={quickLinks.find(link => link.action).action}
                                        className="text-xs text-teal-600 hover:text-teal-800 hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                                    >
                                        <span>{quickLinks.find(link => link.action).icon}</span>
                                        {quickLinks.find(link => link.action).label}
                                    </button>
                                )}

                                {/* Find Bundles — wholesale only */}
                                {window.location.pathname.includes('wholesale') && deal.marketplace !== 'ebay' && (
                                    <FindBundlesButton deal={deal} />
                                )}

                                {deal.marketplace === 'amazon' && data.asin && (
                                    <div className="flex items-center gap-1">
                                        <span className="font-semibold">ASIN:</span>
                                        <span>{data.asin}</span>
                                        <CopyButton text={data.asin} />
                                    </div>
                                )}

                                {deal.marketplace === 'walmart' && data.item_id && (
                                    <div className="flex items-center gap-1">
                                        <span className="font-semibold">Item ID:</span>
                                        <span>{data.item_id}</span>
                                        <CopyButton text={data.item_id} />
                                    </div>
                                )}

                                {(
                                    <div className={`flex items-center gap-1${(!deal.upc || deal.upc === "N/A") ? " text-red-700" : ""}`}>
                                        <span className="font-semibold">UPC:</span>
                                        <span>{deal.upc || "N/A"}</span>
                                        <CopyButton text={deal.upc} />
                                    </div>
                                )}

                                {deal.source_link && (
                                    /^https?:\/\//i.test(deal.source_link) ? (
                                        <a
                                            href={deal.source_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 text-teal-600 hover:text-teal-800 hover:underline font-semibold"
                                        >
                                            <span>🔗</span> Source Link
                                        </a>
                                    ) : (
                                        <span
                                            className="flex items-center gap-1 text-gray-600 hover:text-gray-800 font-semibold"
                                        >
                                            <span>🏷️</span> SKU: {deal.source_link} <CopyButton text={deal.source_link} />
                                        </span>
                                    )
                                )}

                                {/* Hide Deal Button */}
                                {onHideDeal && (
                                    <button
                                        onClick={(e) => {
                                            onHideDeal(deal.id, false);
                                        }}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            onHideDeal(deal.id, true);
                                        }}
                                        className="text-red-600 hover:text-red-800 hover:underline font-semibold cursor-pointer flex items-center gap-1"
                                        title="Click to remove (with confirmation), Right-click to remove immediately"
                                    >
                                        <span>🗑️</span>
                                        Remove Deal
                                    </button>
                                )}

                                {/* Date Info */}
                                {(() => {
                                    const parseDate = (d) => { try { const dt = new Date(d.replace(' ', 'T') + (/(Z|[+-]\d{2}:\d{2})$/.test(d) ? '' : 'Z')); return isNaN(dt) ? new Date(d) : dt; } catch { return new Date(d); } };
                                    const formatDays = (date) => { const d = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24)); return d === 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d}d ago`; };

                                    const isStorefront = window.location.pathname.includes('storefront-scan');

                                    if (isStorefront) {
                                        return (
                                            <div className="flex items-center gap-1 text-gray-500">
                                                <span className="font-semibold">First Seen:</span>
                                                <span>{formatDays(parseDate(deal.created_at))}</span>
                                            </div>
                                        );
                                    }

                                    const dealDate = parseDate(deal.updated_at || deal.created_at);
                                    const daysDiff = Math.floor((new Date() - dealDate) / (1000 * 60 * 60 * 24));
                                    const isOld = daysDiff >= 3;
                                    return (
                                        <div className={`flex items-center gap-1 ${isOld ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
                                            {isOld && <span>⚠️</span>}
                                            <span className="font-semibold">{deal.updated_at ? 'Updated:' : 'Created:'}</span>
                                            <span className="text-xs">{daysDiff === 0 ? 'Today' : daysDiff === 1 ? 'Yesterday' : `${daysDiff}d ago`}</span>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Quick Links with Keepa */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {quickLinks.filter(link => !link.action).map((link, idx) => (
                                    <div key={idx} className="relative keepa-chart-container">
                                        <a
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-teal-600 hover:text-teal-800 hover:underline flex items-center gap-1 font-semibold keepa-trigger"
                                            onMouseEnter={() => link.showChart && fetchKeepaChart()}
                                        >
                                            <span>{link.showChart ? <img src="icons/keepa.png" className="w-4 h-4 mt-0.5" /> : link.icon}</span>
                                            {link.label}
                                            {link.showChart && (
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setShowKeepaSettings(true);
                                                    }}
                                                    className="ml-1 text-gray-500 hover:text-gray-700 text-xs"
                                                    title="Configure Keepa chart"
                                                >
                                                    ⚙️
                                                </button>
                                            )}
                                        </a>

                                        {/* Keepa Chart Hover Popup */}
                                        {link.showChart && deal.marketplace === 'amazon' && (
                                            <div className="keepa-popup absolute right-0 bottom-full hidden z-[60] cursor-pointer"
                                                onClick={() => window.open(link.url)}
                                                style={{ minWidth: '600px', maxWidth: '700px' }}>
                                                <div className="bg-white border-2 border-teal-300 rounded-lg shadow-2xl p-3">
                                                    <div className="text-xs font-semibold text-gray-700 mb-2 text-center border-b pb-1">
                                                        Keepa Price History ({JSON.parse(localStorage.getItem('keepa_chart_settings') || '{"range":90}').range} days)
                                                    </div>
                                                    {(() => {
                                                        const savedSettings = JSON.parse(localStorage.getItem('keepa_chart_settings') || '{"range":90,"salesrank":true,"amazon":true,"new":true,"bb":true,"fba":true}');
                                                        const cacheKey = `${data.asin}_${savedSettings.range}_${savedSettings.salesrank}_${savedSettings.amazon}_${savedSettings.new}_${savedSettings.bb}_${savedSettings.fba}`;
                                                        const cachedUrl = keepaChartCache[cacheKey];

                                                        if (keepaChartLoading) {
                                                            return (
                                                                <div className="flex items-center justify-center py-8">
                                                                    <RefreshCw className="animate-spin text-teal-600" size={24} />
                                                                    <span className="ml-2 text-sm text-gray-600">Loading chart...</span>
                                                                </div>
                                                            );
                                                        } else if (cachedUrl === 'ERROR') {
                                                            return (
                                                                <div className="text-center py-4 text-sm text-red-600">
                                                                    Failed to load chart. Check console for details.
                                                                </div>
                                                            );
                                                        } else if (cachedUrl) {
                                                            return (
                                                                <img
                                                                    src={cachedUrl}
                                                                    alt="Keepa Chart"
                                                                    style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                                                                    onError={(e) => {
                                                                        console.error('Image failed to load:', cachedUrl);
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            );
                                                        } else {
                                                            return (
                                                                <div className="text-center py-4 text-sm text-gray-500">
                                                                    Hover to load chart
                                                                </div>
                                                            );
                                                        }
                                                    })()}
                                                    <div className="absolute -bottom-2 right-8">
                                                        <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-teal-300"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {
                showMultiSourcePopup && (
                    <MultiSourcePopup
                        allSources={allSources}
                        couponAmount={couponAmount}
                        fulfillmentType={fulfillmentType}
                        onClose={() => setShowMultiSourcePopup(false)}
                        onRefreshGated={onRefreshGated}
                        refreshing={refreshing}
                        onSettingsChange={onSettingsChange}
                        amazonLinked={amazonLinked}
                    />
                )
            }

            {showSearchingPopup && (
                <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                            <RefreshCw className="text-blue-500 animate-spin" size={28} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Still Looking for a Supplier!</h3>
                        <p className="text-gray-600 text-sm mb-4">
                            We're scanning suppliers in the background. While you wait, you can search manually using the <strong>Universal Reverse Scan</strong> — our Chrome extension that finds suppliers instantly.
                        </p>
                        <div className="rounded-xl overflow-hidden mb-5" style={{ aspectRatio: '16/9' }}>
                            <iframe
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/sFWdAhKzLuw"
                                title="Universal Reverse Scan Tutorial"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                                style={{ display: 'block', width: '100%', height: '100%', minHeight: 160 }}
                            />
                        </div>
                        <button
                            onClick={() => {
                                setShowSearchingPopup(false);
                                const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';
                                const EXTidentifier = deal.marketplace === 'amazon'
                                    ? deal?.marketplace_data?.asin
                                    : deal.marketplace === 'walmart'
                                        ? deal?.marketplace_data?.item_id
                                        : deal?.marketplace_data?.upc || '';
                                if (window.chrome?.runtime?.sendMessage && EXT_ID) {
                                    window.chrome.runtime.sendMessage(EXT_ID, {
                                        type: 'MSP_SEARCH_INTENT',
                                        marketplace: deal.marketplace,
                                        identifier: EXTidentifier,
                                        title: deal.title,
                                        deal,
                                    });
                                }
                                const title = deal.title || deal.marketplace_data?.title || deal.marketplace_data?.name || '';
                                if (title) {
                                    window.open(`https://www.google.com/search?q=${encodeURIComponent(title)}&gl=us&hl=en&cr=countryUS`, '_blank');
                                }
                            }}
                            className="w-full px-4 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white font-semibold rounded-xl hover:opacity-90 transition mb-3"
                        >
                            🔍 Do Universal Reverse Scan
                        </button>
                        <button
                            onClick={() => setShowSearchingPopup(false)}
                            className="w-full px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50 transition"
                        >
                            Close — I'll wait
                        </button>
                    </div>
                </div>
            )}

            {
                showCashbackModal && (
                    <CashbackModal
                        supplierName={deal.supplier_name}
                        onClose={() => setShowCashbackModal(false)}
                    />
                )
            }

            {showPopupBlockedModal && (
                <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center" onClick={() => setShowPopupBlockedModal(false)}>
                    <div className="bg-white rounded-lg shadow-2xl p-5 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
                        <div className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                            ⚠️ Popup Blocked
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            Your browser blocked the second link. To open both links at once:
                        </p>
                        <ol className="text-sm text-gray-700 space-y-2 mb-4 list-decimal list-inside">
                            <li>Click the <strong>blocked popup icon</strong> in Chrome's address bar (right side)</li>
                            <li>Select <strong>"Always allow pop-ups from this site"</strong></li>
                            <li>Click <strong>Done</strong>, then try again</li>
                        </ol>
                        <div className="border-t pt-3 flex items-center justify-between">
                            <a
                                href={(() => {
                                    const d = deal.marketplace_data || {};
                                    return deal.marketplace === 'amazon' && d.asin
                                        ? `https://www.amazon${amazonDomain}/dp/${d.asin}`
                                        : deal.marketplace === 'walmart' && d.item_id
                                            ? `https://www.walmart.com/ip/${d.item_id}`
                                            : deal.marketplace === 'ebay' && d.ebay_search_link
                                                ? d.ebay_search_link : '#';
                                })()}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-teal-600 hover:text-teal-800 font-semibold underline"
                                onClick={() => setShowPopupBlockedModal(false)}
                            >
                                Open marketplace link manually
                            </a>
                            <button
                                onClick={() => setShowPopupBlockedModal(false)}
                                className="text-sm text-gray-500 hover:text-gray-700 font-semibold"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default DealCard;